import { useState } from 'react';
import { toast } from '@theonefamily/ui';
import { useApi } from '../hooks/useApi.js';
import { post, del } from '../lib/api.js';

type VishingScenario =
  | 'it_password_reset'
  | 'ceo_urgent_wire'
  | 'bank_account_verify'
  | 'vendor_invoice'
  | 'hr_benefits_update';

type VishingOutcome =
  | 'no_answer'
  | 'hung_up_immediately'
  | 'engaged'
  | 'provided_info'
  | 'suspicious_and_reported'
  | 'transferred'
  | 'pending';

interface VishingCampaign {
  id: string;
  name: string;
  scenario: VishingScenario;
  caller_id_name: string;
  caller_id_number?: string;
  target_users: Array<{
    user_id: string;
    user_email: string;
    phone_number?: string;
    name?: string;
    department?: string;
  }>;
  status: 'draft' | 'scheduled' | 'active' | 'completed';
  scheduled_at?: string;
  started_at?: string;
  completed_at?: string;
  total_targets: number;
  calls_placed: number;
  calls_answered: number;
  provided_info_count: number;
  reported_count: number;
  report_rate?: number;
  created_at: string;
  result_summary?: {
    total_targets: number;
    calls_placed: number;
    calls_answered: number;
    provided_info: number;
    reported: number;
    hung_up: number;
    engaged: number;
    transferred: number;
  };
}

interface VishingCallResult {
  id: string;
  campaign_id: string;
  user_id: string;
  user_email: string;
  user_name?: string;
  department?: string;
  phone_number?: string;
  outcome: VishingOutcome;
  call_duration_seconds?: number;
  outcome_recorded_at?: string;
  notes?: string;
  remedial_training_assigned: boolean;
  training_campaign_id?: string;
  created_at: string;
}

interface ScenarioInfo {
  key: VishingScenario;
  caller_id: string;
  description: string;
  risk_level: string;
  script_summary: string;
}

interface CampaignsResponse {
  data: VishingCampaign[];
  hasMore?: boolean;
}

interface ScenariosResponse {
  data: ScenarioInfo[];
}

interface ResultsResponse {
  data: VishingCallResult[];
  hasMore?: boolean;
}

const SCENARIO_LABELS: Record<VishingScenario, string> = {
  it_password_reset: 'IT Password Reset',
  ceo_urgent_wire: 'CEO Urgent Wire',
  bank_account_verify: 'Bank Account Verify',
  vendor_invoice: 'Vendor Invoice',
  hr_benefits_update: 'HR Benefits Update',
};

function outcomeColor(outcome: VishingOutcome): string {
  switch (outcome) {
    case 'provided_info':
      return 'bg-red-100 text-red-700';
    case 'engaged':
      return 'bg-orange-100 text-orange-700';
    case 'suspicious_and_reported':
      return 'bg-emerald-100 text-emerald-700';
    case 'hung_up_immediately':
      return 'bg-blue-100 text-blue-700';
    case 'transferred':
      return 'bg-purple-100 text-purple-700';
    case 'no_answer':
      return 'bg-gray-100 text-gray-600';
    case 'pending':
      return 'bg-yellow-100 text-yellow-700';
    default:
      return 'bg-gray-100 text-gray-600';
  }
}

function outcomeLabel(outcome: VishingOutcome): string {
  switch (outcome) {
    case 'provided_info': return 'Provided Info';
    case 'engaged': return 'Engaged';
    case 'suspicious_and_reported': return 'Reported';
    case 'hung_up_immediately': return 'Hung Up';
    case 'transferred': return 'Transferred';
    case 'no_answer': return 'No Answer';
    case 'pending': return 'Pending';
    default: return outcome;
  }
}

function statusBadge(status: string) {
  const colors =
    status === 'active'
      ? 'bg-emerald-100 text-emerald-700'
      : status === 'completed'
      ? 'bg-blue-100 text-blue-700'
      : status === 'scheduled'
      ? 'bg-yellow-100 text-yellow-700'
      : 'bg-gray-100 text-gray-600';
  return (
    <span className={`px-2 py-1 text-xs font-medium rounded-full ${colors} capitalize`}>
      {status}
    </span>
  );
}

function riskBadge(risk: string) {
  const colors =
    risk === 'critical'
      ? 'bg-red-100 text-red-700'
      : risk === 'high'
      ? 'bg-orange-100 text-orange-700'
      : 'bg-yellow-100 text-yellow-700';
  return (
    <span className={`px-2 py-1 text-xs font-medium rounded-full ${colors} capitalize`}>
      {risk}
    </span>
  );
}

function reportRateColor(rate: number): string {
  if (rate >= 0.5) return 'text-emerald-600';
  if (rate >= 0.25) return 'text-yellow-600';
  return 'text-red-600';
}

export function VishingPage() {
  const { data: campaignsData, loading, error, refresh } = useApi<CampaignsResponse>('/protect/vishing');
  const { data: scenariosData } = useApi<ScenariosResponse>('/protect/vishing?action=scenarios');

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [activeTab, setActiveTab] = useState<'campaigns' | 'scenarios'>('campaigns');
  const [selectedCampaign, setSelectedCampaign] = useState<VishingCampaign | null>(null);
  const [showResultsPanel, setShowResultsPanel] = useState(false);
  const [resultsData, setResultsData] = useState<VishingCallResult[] | null>(null);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // Create form state
  const [formName, setFormName] = useState('');
  const [formScenario, setFormScenario] = useState<VishingScenario | ''>('');
  const [formCallerIdName, setFormCallerIdName] = useState('');
  const [formCallerIdNumber, setFormCallerIdNumber] = useState('');
  const [formTargetEmails, setFormTargetEmails] = useState('');
  const [formScheduledAt, setFormScheduledAt] = useState('');
  const [formSubmitting, setFormSubmitting] = useState(false);

  // Record outcome state
  const [showOutcomeModal, setShowOutcomeModal] = useState(false);
  const [outcomeResult, setOutcomeResult] = useState<VishingCallResult | null>(null);
  const [outcomeValue, setOutcomeValue] = useState<VishingOutcome | ''>('');
  const [outcomeDuration, setOutcomeDuration] = useState('');
  const [outcomeNotes, setOutcomeNotes] = useState('');
  const [outcomeSubmitting, setOutcomeSubmitting] = useState(false);

  const campaigns = campaignsData?.data ?? [];
  const scenarios = scenariosData?.data ?? [];

  const activeCampaigns = campaigns.filter((c) => c.status !== 'draft');
  const avgReportRate =
    activeCampaigns.length > 0
      ? activeCampaigns.reduce((s, c) => s + (c.report_rate ?? 0), 0) / activeCampaigns.length
      : 0;
  const avgInfoRate =
    activeCampaigns.length > 0
      ? activeCampaigns.reduce(
          (s, c) =>
            s +
            (c.calls_answered > 0
              ? c.provided_info_count / c.calls_answered
              : 0),
          0,
        ) / activeCampaigns.length
      : 0;

  function handleScenarioSelect(scenario: VishingScenario) {
    setFormScenario(scenario);
    const info = scenarios.find((s) => s.key === scenario);
    if (info && !formCallerIdName) {
      setFormCallerIdName(info.caller_id);
    }
    setActiveTab('campaigns');
    setShowCreateModal(true);
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!formScenario) return;
    setFormSubmitting(true);
    try {
      const emails = formTargetEmails
        .split(',')
        .map((e) => e.trim())
        .filter(Boolean);
      const targetUsers = emails.map((email) => ({
        user_id: email,
        user_email: email,
      }));

      await post('/protect/vishing', {
        name: formName.trim(),
        scenario: formScenario,
        caller_id_name: formCallerIdName.trim() || undefined,
        caller_id_number: formCallerIdNumber.trim() || undefined,
        target_users: targetUsers,
        scheduled_at: formScheduledAt || undefined,
      });

      setShowCreateModal(false);
      resetForm();
      refresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to create campaign');
    } finally {
      setFormSubmitting(false);
    }
  }

  function resetForm() {
    setFormName('');
    setFormScenario('');
    setFormCallerIdName('');
    setFormCallerIdNumber('');
    setFormTargetEmails('');
    setFormScheduledAt('');
  }

  async function handleLaunch(id: string) {
    setActionLoading(id);
    try {
      await post(`/protect/vishing/${id}?action=launch`);
      refresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to launch campaign');
    } finally {
      setActionLoading(null);
    }
  }

  async function handleComplete(id: string) {
    setActionLoading(id);
    try {
      await post(`/protect/vishing/${id}?action=complete`);
      refresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to complete campaign');
    } finally {
      setActionLoading(null);
    }
  }

  async function handleDelete(id: string) {
    if (!confirm('Delete this vishing campaign?')) return;
    setActionLoading(id);
    try {
      await del(`/protect/vishing/${id}`);
      refresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete campaign');
    } finally {
      setActionLoading(null);
    }
  }

  async function openResults(campaign: VishingCampaign) {
    setSelectedCampaign(campaign);
    setShowResultsPanel(true);
    setResultsLoading(true);
    try {
      const res = await import('../lib/api.js').then((m) =>
        m.get<ResultsResponse>(`/protect/vishing/${campaign.id}?action=results`),
      );
      setResultsData(res.data);
    } catch {
      setResultsData([]);
    } finally {
      setResultsLoading(false);
    }
  }

  function openRecordOutcome(result: VishingCallResult) {
    setOutcomeResult(result);
    setOutcomeValue(result.outcome === 'pending' ? '' : result.outcome);
    setOutcomeDuration(result.call_duration_seconds?.toString() ?? '');
    setOutcomeNotes(result.notes ?? '');
    setShowOutcomeModal(true);
  }

  async function handleRecordOutcome(e: React.FormEvent) {
    e.preventDefault();
    if (!outcomeResult || !outcomeValue || !selectedCampaign) return;
    setOutcomeSubmitting(true);
    try {
      await post(`/protect/vishing/${selectedCampaign.id}?action=record_outcome`, {
        user_id: outcomeResult.user_id,
        outcome: outcomeValue,
        call_duration_seconds: outcomeDuration ? parseInt(outcomeDuration, 10) : undefined,
        notes: outcomeNotes || undefined,
      });
      setShowOutcomeModal(false);
      setOutcomeResult(null);
      setOutcomeValue('');
      setOutcomeDuration('');
      setOutcomeNotes('');
      // Refresh results
      await openResults(selectedCampaign);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to record outcome');
    } finally {
      setOutcomeSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading vishing campaigns...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-700">{error}</p>
        <button onClick={refresh} className="mt-2 text-sm text-red-600 underline">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Vishing Simulation</h2>
          <p className="text-gray-600 mt-1">
            Test user resilience to voice-based social engineering attacks.
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-md hover:bg-emerald-700"
        >
          + New Campaign
        </button>
      </div>

      {/* Stats Row */}
      {activeCampaigns.length > 0 && (
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <p className="text-sm text-gray-500">Campaigns Run</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{activeCampaigns.length}</p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <p className="text-sm text-gray-500">Avg Report Rate</p>
            <p className={`text-2xl font-bold mt-1 ${reportRateColor(avgReportRate)}`}>
              {Math.round(avgReportRate * 100)}%
            </p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <p className="text-sm text-gray-500">Info Provided Rate</p>
            <p className={`text-2xl font-bold mt-1 ${avgInfoRate > 0.1 ? 'text-red-600' : avgInfoRate > 0.05 ? 'text-yellow-600' : 'text-emerald-600'}`}>
              {Math.round(avgInfoRate * 100)}%
            </p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <p className="text-sm text-gray-500">Total Calls Placed</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">
              {activeCampaigns.reduce((s, c) => s + c.calls_placed, 0)}
            </p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 mb-4 border-b border-gray-200">
        <button
          onClick={() => setActiveTab('campaigns')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
            activeTab === 'campaigns'
              ? 'border-emerald-600 text-emerald-700'
              : 'border-transparent text-gray-600 hover:text-gray-900'
          }`}
        >
          Campaigns
        </button>
        <button
          onClick={() => setActiveTab('scenarios')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
            activeTab === 'scenarios'
              ? 'border-emerald-600 text-emerald-700'
              : 'border-transparent text-gray-600 hover:text-gray-900'
          }`}
        >
          Scenarios
        </button>
      </div>

      {/* Scenarios Tab */}
      {activeTab === 'scenarios' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {scenarios.map((s) => (
            <div key={s.key} className="bg-white rounded-lg border border-gray-200 p-4 flex flex-col">
              <div className="flex items-start justify-between mb-2">
                <p className="font-semibold text-gray-900 text-sm">{SCENARIO_LABELS[s.key]}</p>
                {riskBadge(s.risk_level)}
              </div>
              <p className="text-xs text-gray-500 mb-1">
                <span className="font-medium">Caller ID:</span> {s.caller_id}
              </p>
              <p className="text-sm text-gray-600 mb-3 flex-1">{s.description}</p>
              <p className="text-xs text-gray-400 italic mb-3">{s.script_summary}</p>
              <button
                onClick={() => handleScenarioSelect(s.key)}
                className="mt-auto px-3 py-2 bg-emerald-600 text-white text-xs font-medium rounded-md hover:bg-emerald-700 w-full"
              >
                Use This Scenario
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Campaigns Tab */}
      {activeTab === 'campaigns' && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Campaign</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Scenario</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Status</th>
                <th className="text-right px-4 py-3 font-medium text-gray-600">Targets</th>
                <th className="text-right px-4 py-3 font-medium text-gray-600">Calls</th>
                <th className="text-right px-4 py-3 font-medium text-gray-600">Report Rate</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Created</th>
                <th className="text-right px-4 py-3 font-medium text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {campaigns.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                    No vishing campaigns yet. Create one to get started, or browse Scenarios.
                  </td>
                </tr>
              ) : (
                campaigns.map((c) => {
                  const rr = c.report_rate ?? 0;
                  return (
                    <tr
                      key={c.id}
                      className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
                      onClick={() => openResults(c)}
                    >
                      <td className="px-4 py-3 font-medium text-gray-900">{c.name}</td>
                      <td className="px-4 py-3 text-gray-600">{SCENARIO_LABELS[c.scenario]}</td>
                      <td className="px-4 py-3">{statusBadge(c.status)}</td>
                      <td className="px-4 py-3 text-right text-gray-600">{c.total_targets}</td>
                      <td className="px-4 py-3 text-right text-gray-600">{c.calls_placed}</td>
                      <td className="px-4 py-3 text-right">
                        {c.status === 'draft' ? (
                          <span className="text-gray-400">—</span>
                        ) : (
                          <span className={`font-medium ${reportRateColor(rr)}`}>
                            {Math.round(rr * 100)}%
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-500">
                        {new Date(c.created_at).toLocaleDateString()}
                      </td>
                      <td
                        className="px-4 py-3 text-right"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {c.status === 'draft' && (
                          <>
                            <button
                              onClick={() => handleLaunch(c.id)}
                              disabled={actionLoading === c.id}
                              className="text-xs text-emerald-600 hover:text-emerald-800 mr-2 disabled:opacity-50"
                            >
                              Launch
                            </button>
                            <button
                              onClick={() => handleDelete(c.id)}
                              disabled={actionLoading === c.id}
                              className="text-xs text-red-600 hover:text-red-800 disabled:opacity-50"
                            >
                              Delete
                            </button>
                          </>
                        )}
                        {c.status === 'active' && (
                          <button
                            onClick={() => handleComplete(c.id)}
                            disabled={actionLoading === c.id}
                            className="text-xs text-blue-600 hover:text-blue-800 disabled:opacity-50"
                          >
                            Complete
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Campaign Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">New Vishing Campaign</h3>
            </div>
            <form onSubmit={handleCreate} className="px-6 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Campaign Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  placeholder="Q1 Vishing Test — Finance Team"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Scenario <span className="text-red-500">*</span>
                </label>
                <div className="space-y-2">
                  {scenarios.length === 0
                    ? (['it_password_reset', 'ceo_urgent_wire', 'bank_account_verify', 'vendor_invoice', 'hr_benefits_update'] as VishingScenario[]).map((key) => (
                        <label key={key} className="flex items-center gap-3 p-2 border border-gray-200 rounded-md cursor-pointer hover:bg-gray-50">
                          <input
                            type="radio"
                            name="scenario"
                            value={key}
                            checked={formScenario === key}
                            onChange={() => {
                              setFormScenario(key);
                            }}
                            className="text-emerald-600"
                          />
                          <span className="text-sm text-gray-800">{SCENARIO_LABELS[key]}</span>
                        </label>
                      ))
                    : scenarios.map((s) => (
                        <label
                          key={s.key}
                          className={`flex items-start gap-3 p-3 border rounded-md cursor-pointer hover:bg-gray-50 ${
                            formScenario === s.key ? 'border-emerald-400 bg-emerald-50' : 'border-gray-200'
                          }`}
                        >
                          <input
                            type="radio"
                            name="scenario"
                            value={s.key}
                            checked={formScenario === s.key}
                            onChange={() => {
                              setFormScenario(s.key);
                              if (!formCallerIdName) setFormCallerIdName(s.caller_id);
                            }}
                            className="mt-0.5 text-emerald-600"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-gray-900">
                                {SCENARIO_LABELS[s.key]}
                              </span>
                              {riskBadge(s.risk_level)}
                            </div>
                            <p className="text-xs text-gray-500 mt-0.5">{s.script_summary}</p>
                          </div>
                        </label>
                      ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Caller ID Name
                  </label>
                  <input
                    type="text"
                    value={formCallerIdName}
                    onChange={(e) => setFormCallerIdName(e.target.value)}
                    placeholder="IT Help Desk"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Caller ID Number
                  </label>
                  <input
                    type="tel"
                    value={formCallerIdNumber}
                    onChange={(e) => setFormCallerIdNumber(e.target.value)}
                    placeholder="+15555550100"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Target Users <span className="text-red-500">*</span>
                </label>
                <p className="text-xs text-gray-500 mb-1">Enter comma-separated email addresses</p>
                <textarea
                  value={formTargetEmails}
                  onChange={(e) => setFormTargetEmails(e.target.value)}
                  placeholder="alice@company.com, bob@company.com, carol@company.com"
                  rows={3}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm resize-none"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Scheduled At (optional)
                </label>
                <input
                  type="datetime-local"
                  value={formScheduledAt}
                  onChange={(e) => setFormScheduledAt(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                />
              </div>

              <div className="flex gap-2 pt-2">
                <button
                  type="submit"
                  disabled={formSubmitting || !formScenario}
                  className="px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-md hover:bg-emerald-700 disabled:opacity-50"
                >
                  {formSubmitting ? 'Creating...' : 'Create Campaign'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    resetForm();
                  }}
                  className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-200"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Results Slide-out Panel */}
      {showResultsPanel && selectedCampaign && (
        <div className="fixed inset-0 bg-black/40 flex justify-end z-50">
          <div className="bg-white w-full max-w-2xl h-full overflow-y-auto shadow-xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 sticky top-0 bg-white z-10">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{selectedCampaign.name}</h3>
                <p className="text-sm text-gray-500">
                  {SCENARIO_LABELS[selectedCampaign.scenario]} &bull; {statusBadge(selectedCampaign.status)}
                </p>
              </div>
              <button
                onClick={() => {
                  setShowResultsPanel(false);
                  setSelectedCampaign(null);
                  setResultsData(null);
                }}
                className="text-gray-400 hover:text-gray-600 text-xl font-bold"
              >
                &times;
              </button>
            </div>

            {/* Summary Stats */}
            <div className="px-6 py-4 border-b border-gray-100">
              <div className="grid grid-cols-4 gap-3">
                <div className="text-center">
                  <p className="text-2xl font-bold text-gray-900">{selectedCampaign.calls_placed}</p>
                  <p className="text-xs text-gray-500">Calls Placed</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-gray-900">{selectedCampaign.calls_answered}</p>
                  <p className="text-xs text-gray-500">Answered</p>
                </div>
                <div className="text-center">
                  <p className={`text-2xl font-bold ${selectedCampaign.provided_info_count > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                    {selectedCampaign.provided_info_count}
                  </p>
                  <p className="text-xs text-gray-500">Provided Info</p>
                </div>
                <div className="text-center">
                  <p className={`text-2xl font-bold ${reportRateColor(selectedCampaign.report_rate ?? 0)}`}>
                    {Math.round((selectedCampaign.report_rate ?? 0) * 100)}%
                  </p>
                  <p className="text-xs text-gray-500">Report Rate</p>
                </div>
              </div>
            </div>

            {/* Results Table */}
            <div className="px-6 py-4">
              <h4 className="text-sm font-semibold text-gray-700 mb-3">Call Results</h4>
              {resultsLoading ? (
                <div className="text-center py-8 text-gray-400 text-sm">Loading results...</div>
              ) : resultsData && resultsData.length > 0 ? (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-2 font-medium text-gray-600">User</th>
                      <th className="text-left py-2 font-medium text-gray-600">Dept</th>
                      <th className="text-left py-2 font-medium text-gray-600">Outcome</th>
                      <th className="text-right py-2 font-medium text-gray-600">Duration</th>
                      <th className="text-center py-2 font-medium text-gray-600">Training</th>
                      <th className="text-right py-2 font-medium text-gray-600"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {resultsData.map((r) => (
                      <tr key={r.id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-2 text-gray-900">
                          <p className="font-medium">{r.user_name ?? r.user_email}</p>
                          {r.user_name && (
                            <p className="text-xs text-gray-500">{r.user_email}</p>
                          )}
                        </td>
                        <td className="py-2 text-gray-500">{r.department ?? '—'}</td>
                        <td className="py-2">
                          <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${outcomeColor(r.outcome)}`}>
                            {outcomeLabel(r.outcome)}
                          </span>
                        </td>
                        <td className="py-2 text-right text-gray-500">
                          {r.call_duration_seconds != null ? `${r.call_duration_seconds}s` : '—'}
                        </td>
                        <td className="py-2 text-center">
                          {r.remedial_training_assigned ? (
                            <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
                              Assigned
                            </span>
                          ) : (
                            <span className="text-gray-300">—</span>
                          )}
                        </td>
                        <td className="py-2 text-right">
                          <button
                            onClick={() => openRecordOutcome(r)}
                            className="text-xs text-emerald-600 hover:text-emerald-800"
                          >
                            Record
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="text-center py-8 text-gray-400 text-sm">
                  No call results recorded yet. Launch the campaign to place calls.
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Record Outcome Modal */}
      {showOutcomeModal && outcomeResult && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Record Call Outcome</h3>
              <p className="text-sm text-gray-500 mt-1">{outcomeResult.user_email}</p>
            </div>
            <form onSubmit={handleRecordOutcome} className="px-6 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Outcome <span className="text-red-500">*</span>
                </label>
                <div className="space-y-1">
                  {(
                    [
                      'no_answer',
                      'hung_up_immediately',
                      'engaged',
                      'provided_info',
                      'suspicious_and_reported',
                      'transferred',
                    ] as VishingOutcome[]
                  ).map((o) => (
                    <label
                      key={o}
                      className={`flex items-center gap-3 px-3 py-2 border rounded-md cursor-pointer hover:bg-gray-50 ${
                        outcomeValue === o ? 'border-emerald-400 bg-emerald-50' : 'border-gray-200'
                      }`}
                    >
                      <input
                        type="radio"
                        name="outcome"
                        value={o}
                        checked={outcomeValue === o}
                        onChange={() => setOutcomeValue(o)}
                        className="text-emerald-600"
                      />
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${outcomeColor(o)}`}>
                        {outcomeLabel(o)}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Call Duration (seconds)
                </label>
                <input
                  type="number"
                  value={outcomeDuration}
                  onChange={(e) => setOutcomeDuration(e.target.value)}
                  placeholder="e.g. 45"
                  min="0"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes
                </label>
                <textarea
                  value={outcomeNotes}
                  onChange={(e) => setOutcomeNotes(e.target.value)}
                  placeholder="Optional observations..."
                  rows={2}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm resize-none"
                />
              </div>
              {(outcomeValue === 'provided_info' || outcomeValue === 'engaged') && (
                <div className="bg-amber-50 border border-amber-200 rounded-md px-3 py-2 text-xs text-amber-700">
                  Remedial training will be automatically assigned to this user.
                </div>
              )}
              <div className="flex gap-2 pt-1">
                <button
                  type="submit"
                  disabled={outcomeSubmitting || !outcomeValue}
                  className="px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-md hover:bg-emerald-700 disabled:opacity-50"
                >
                  {outcomeSubmitting ? 'Saving...' : 'Save Outcome'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowOutcomeModal(false);
                    setOutcomeResult(null);
                  }}
                  className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-200"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
